import React, { useState, useRef } from 'react';
import { 
  User, Building, Mail, Phone, MapPin, Globe, Calendar, 
  Package, Camera, Upload, Save, QrCode, CheckCircle, 
  X, AlertTriangle, FileText, Clipboard, Check
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useEmail } from '../../hooks/useEmail';

const MerchantAcquisitionForm = () => {
  const { sendNotification } = useEmail();
  const [formData, setFormData] = useState({
    organization: '',
    representative: '',
    representativeEmail: '',
    address: '',
    email: '',
    phone: '',
    mobile: '',
    products: '',
    yearEstablished: '',
    specialties: [''],
    location: '',
    city: 'Dhaka',
    description: ''
  });
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [qrCodeCopied, setQrCodeCopied] = useState(false);
  const [photoTaken, setPhotoTaken] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [documentUploaded, setDocumentUploaded] = useState(false);
  const [documentPreview, setDocumentPreview] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraActive, setCameraActive] = useState(false);

  // Generate a slug from organization name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
      .trim();
  };

  // Generate a unique slug by appending a number if needed
  const generateUniqueSlug = async (name: string) => {
    const baseSlug = generateSlug(name);
    let slug = baseSlug;
    let counter = 1;

    // Check if slug exists and increment counter until we find a unique one
    while (true) {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id')
        .eq('slug', slug)
        .maybeSingle();

      if (error) {
        // An unexpected error occurred
        throw error;
      } else if (data === null) {
        // No rows found, slug is unique
        break;
      } else {
        // Slug exists, try with counter
        slug = `${baseSlug}-${counter}`;
        counter++;
      }
    }

    return slug;
  };

  // Generate a subdomain from slug
  const generateSubdomain = (slug: string) => {
    return `${slug}.madeinbd.com`;
  };

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle specialty changes
  const handleSpecialtyChange = (index: number, value: string) => {
    const updatedSpecialties = [...formData.specialties];
    updatedSpecialties[index] = value;
    setFormData(prev => ({
      ...prev,
      specialties: updatedSpecialties
    }));
  };

  // Add a new specialty field
  const addSpecialty = () => {
    setFormData(prev => ({
      ...prev,
      specialties: [...prev.specialties, '']
    }));
  };

  // Remove a specialty field
  const removeSpecialty = (index: number) => {
    if (formData.specialties.length > 1) {
      const updatedSpecialties = [...formData.specialties];
      updatedSpecialties.splice(index, 1);
      setFormData(prev => ({
        ...prev,
        specialties: updatedSpecialties
      }));
    }
  };

  // Start camera for taking photo
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('Could not access camera. Please make sure you have granted camera permissions.');
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
  };

  // Take photo
  const takePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to data URL
        const dataUrl = canvas.toDataURL('image/jpeg');
        setPhotoPreview(dataUrl);
        setPhotoTaken(true);
        
        // Stop camera
        stopCamera();
      }
    }
  };

  // Handle document upload
  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setDocumentPreview(event.target.result as string);
          setDocumentUploaded(true);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated and is an admin
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('You must be logged in to perform this action.');
      }
      
      // Verify user is admin
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('account_type')
        .eq('id', user.id)
        .single();
      
      if (userError) {
        throw new Error('Unable to verify user permissions.');
      }
      
      if (userData.account_type !== 'admin') {
        throw new Error('You do not have permission to create suppliers. Only admin users can perform this action.');
      }
      
      // Generate unique slug and subdomain
      const slug = await generateUniqueSlug(formData.organization);
      const subdomain = generateSubdomain(slug);
      
      // In a real application, you would upload the photo and document to storage
      // For this demo, we'll just use the preview URLs
      
      // Create supplier record
      const { data, error } = await supabase
        .from('suppliers')
        .insert({
          organization: formData.organization,
          representative: formData.representative,
          address: formData.address,
          email: formData.email,
          phone: formData.phone,
          mobile: formData.mobile,
          products: formData.products,
          slug,
          subdomain,
          user_id: null, // Explicitly set to null for admin-created suppliers
          verified: false,
          year_established: formData.yearEstablished ? parseInt(formData.yearEstablished) : null,
          rating: 0,
          total_products: 0,
          description: formData.description,
          specialties: formData.specialties.filter(s => s.trim() !== ''),
          location: formData.location,
          city: formData.city
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Generate QR code data
      const qrData = JSON.stringify({
        id: data.id,
        organization: data.organization,
        representative: data.representative,
        timestamp: new Date().toISOString()
      });
      
      setQrCodeData(qrData);
      setFormSubmitted(true);
      
      // Send verification email to the representative
      if (formData.representativeEmail) {
        try {
          // Generate verification link
          const verificationLink = `${window.location.origin}/supplier-verification?id=${data.id}&token=${Date.now().toString(36)}`;
          
          // Send verification email
          await sendNotification(
            formData.representativeEmail,
            formData.representative,
            'Verify Your Supplier Account - Made in BD',
            `Thank you for applying to become a supplier on Made in BD. Your application for ${formData.organization} has been received. Please verify your email address by clicking the button below to continue the registration process.`,
            verificationLink,
            'Verify Email Address'
          );
          setEmailSent(true);
        } catch (emailError) {
          console.error('Error sending notification email:', emailError);
          // Don't set error state here as the form submission was successful
        }
      }
      
    } catch (err: any) {
      console.error('Error submitting form:', err);
      
      // Handle specific error cases
      if (err.code === '23505') {
        if (err.message.includes('suppliers_slug_key')) {
          setError('A supplier with this organization name already exists. Please use a different name or contact support if this is your organization.');
        } else if (err.message.includes('suppliers_subdomain_key')) {
          setError('A subdomain conflict occurred. Please try again or contact support.');
        } else {
          setError('This organization information already exists in our system. Please check if you have already registered or use different details.');
        }
      } else {
        setError('An error occurred while submitting the form. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Copy QR code data to clipboard
  const copyQrCodeData = () => {
    if (qrCodeData) {
      navigator.clipboard.writeText(qrCodeData);
      setQrCodeCopied(true);
      setTimeout(() => setQrCodeCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Merchant Acquisition Form</h1>
              <p className="text-gray-600">Register new suppliers for verification</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {formSubmitted ? (
          <div className="bg-white rounded-lg shadow-sm p-8 max-w-2xl mx-auto">
            <div className="text-center mb-6">
              <div className="bg-green-100 p-3 rounded-full inline-block mb-4">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Supplier Registration Complete</h2>
              <p className="text-gray-600">
                The supplier information has been successfully submitted for verification.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">QR Code for Verification</h3>
              <p className="text-gray-600 mb-4">
                This QR code contains the supplier information. The compliance team will scan this code to verify the business.
              </p>
              
              <div className="flex justify-center mb-6">
                <div className="bg-white p-4 border border-gray-300 rounded-lg">
                  <QrCode className="h-48 w-48 text-gray-900" />
                </div>
              </div>
              
              <div className="flex justify-center">
                <button
                  onClick={copyQrCodeData}
                  className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  {qrCodeCopied ? (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Clipboard className="h-4 w-4" />
                      <span>Copy QR Data</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Next Steps</h3>
              <ol className="space-y-3 text-gray-600">
                <li className="flex items-start space-x-2">
                  <span className="bg-orange-100 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                  <span>The representative will receive a verification email.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-orange-100 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                  <span>After email verification, the compliance team will review the submitted information.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-orange-100 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                  <span>They will scan the QR code to access and verify the supplier details.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-orange-100 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                  <span>Once verified, the supplier will receive access to their seller dashboard.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="bg-orange-100 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">5</span>
                  <span>The supplier can then start listing their products and receiving orders.</span>
                </li>
              </ol>
            </div>
            
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-800 text-white px-6 py-3 rounded-lg hover:bg-gray-900 transition-colors"
              >
                Register Another Supplier
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <form onSubmit={handleSubmit}>
                  {/* Business Information */}
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                          Organization Name *
                        </label>
                        <div className="relative">
                          <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="organization"
                            name="organization"
                            type="text"
                            required
                            value={formData.organization}
                            onChange={handleChange}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Company name"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="representative" className="block text-sm font-medium text-gray-700 mb-2">
                          Representative Name *
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="representative"
                            name="representative"
                            type="text"
                            required
                            value={formData.representative}
                            onChange={handleChange}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Contact person"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="representativeEmail" className="block text-sm font-medium text-gray-700 mb-2">
                        Representative Email *
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          id="representativeEmail"
                          name="representativeEmail"
                          type="email"
                          required
                          value={formData.representativeEmail}
                          onChange={handleChange}
                          className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="representative@company.com"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">This email will receive verification instructions</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                          Business Address *
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <textarea
                            id="address"
                            name="address"
                            required
                            value={formData.address}
                            onChange={handleChange}
                            rows={3}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Full business address"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="business@example.com"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="phone"
                            name="phone"
                            type="text"
                            value={formData.phone}
                            onChange={handleChange}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Office phone"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                          Mobile Number *
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="mobile"
                            name="mobile"
                            type="text"
                            required
                            value={formData.mobile}
                            onChange={handleChange}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="e.g., 01711123456"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="yearEstablished" className="block text-sm font-medium text-gray-700 mb-2">
                          Year Established
                        </label>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="yearEstablished"
                            name="yearEstablished"
                            type="number"
                            min="1900"
                            max={new Date().getFullYear()}
                            value={formData.yearEstablished}
                            onChange={handleChange}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="e.g., 2010"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                          City *
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <select
                            id="city"
                            name="city"
                            required
                            value={formData.city}
                            onChange={handleChange}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          >
                            <option value="Dhaka">Dhaka</option>
                            <option value="Chittagong">Chittagong</option>
                            <option value="Khulna">Khulna</option>
                            <option value="Rajshahi">Rajshahi</option>
                            <option value="Sylhet">Sylhet</option>
                            <option value="Barisal">Barisal</option>
                            <option value="Rangpur">Rangpur</option>
                            <option value="Mymensingh">Mymensingh</option>
                            <option value="Comilla">Comilla</option>
                            <option value="Narayanganj">Narayanganj</option>
                            <option value="Gazipur">Gazipur</option>
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                          Location Description *
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            id="location"
                            name="location"
                            type="text"
                            required
                            value={formData.location}
                            onChange={handleChange}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="e.g., Dhaka, Bangladesh"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Product Information */}
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h2>
                    <div className="space-y-6">
                      <div>
                        <label htmlFor="products" className="block text-sm font-medium text-gray-700 mb-2">
                          Products & Services *
                        </label>
                        <div className="relative">
                          <Package className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                          <textarea
                            id="products"
                            name="products"
                            required
                            value={formData.products}
                            onChange={handleChange}
                            rows={3}
                            className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Describe the products and services offered"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Specialties
                        </label>
                        <div className="space-y-3">
                          {formData.specialties.map((specialty, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={specialty}
                                onChange={(e) => handleSpecialtyChange(index, e.target.value)}
                                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder={`Specialty ${index + 1}`}
                              />
                              {formData.specialties.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeSpecialty(index)}
                                  className="p-2 text-gray-400 hover:text-red-500"
                                >
                                  <X className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={addSpecialty}
                            className="text-orange-600 hover:text-orange-700 text-sm font-medium flex items-center space-x-1"
                          >
                            <span>+ Add Another Specialty</span>
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                          Business Description
                        </label>
                        <textarea
                          id="description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          rows={4}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                          placeholder="Provide a detailed description of the business"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Photo and Document */}
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Photo & Documentation</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Business Photo */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Business Photo
                        </label>
                        <div className="border border-gray-300 rounded-lg p-4">
                          {photoTaken ? (
                            <div className="space-y-4">
                              <div className="relative">
                                <img 
                                  src={photoPreview || ''} 
                                  alt="Business" 
                                  className="w-full h-48 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPhotoTaken(false);
                                    setPhotoPreview(null);
                                  }}
                                  className="absolute top-2 right-2 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="flex justify-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPhotoTaken(false);
                                    setPhotoPreview(null);
                                    startCamera();
                                  }}
                                  className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm"
                                >
                                  Take New Photo
                                </button>
                              </div>
                            </div>
                          ) : cameraActive ? (
                            <div className="space-y-4">
                              <div className="relative">
                                <video 
                                  ref={videoRef} 
                                  autoPlay 
                                  playsInline 
                                  className="w-full h-48 object-cover rounded-lg"
                                />
                              </div>
                              <div className="flex justify-center space-x-3">
                                <button
                                  type="button"
                                  onClick={takePhoto}
                                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                                >
                                  Capture
                                </button>
                                <button
                                  type="button"
                                  onClick={stopCamera}
                                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center space-y-4">
                              <div className="bg-gray-100 p-8 rounded-lg flex flex-col items-center justify-center">
                                <Camera className="h-12 w-12 text-gray-400 mb-2" />
                                <p className="text-sm text-gray-500">No photo taken</p>
                              </div>
                              <button
                                type="button"
                                onClick={startCamera}
                                className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm"
                              >
                                Take Photo
                              </button>
                            </div>
                          )}
                          <canvas ref={canvasRef} className="hidden" />
                        </div>
                      </div>
                      
                      {/* Business Document */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Business Document
                        </label>
                        <div className="border border-gray-300 rounded-lg p-4">
                          {documentUploaded ? (
                            <div className="space-y-4">
                              <div className="relative">
                                <div className="bg-gray-100 p-4 rounded-lg flex items-center">
                                  <FileText className="h-8 w-8 text-orange-600 mr-3" />
                                  <div>
                                    <p className="font-medium text-gray-900">Document Uploaded</p>
                                    <p className="text-sm text-gray-500">Business registration document</p>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setDocumentUploaded(false);
                                      setDocumentPreview(null);
                                    }}
                                    className="absolute top-2 right-2 p-1 bg-red-100 rounded-full text-red-600 hover:bg-red-200"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                              <div className="flex justify-center">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDocumentUploaded(false);
                                    setDocumentPreview(null);
                                  }}
                                  className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm"
                                >
                                  Upload New Document
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center space-y-4">
                              <div className="bg-gray-100 p-8 rounded-lg flex flex-col items-center justify-center">
                                <FileText className="h-12 w-12 text-gray-400 mb-2" />
                                <p className="text-sm text-gray-500">No document uploaded</p>
                              </div>
                              <div className="relative">
                                <input
                                  type="file"
                                  id="document"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={handleDocumentUpload}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                <button
                                  type="button"
                                  className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm flex items-center justify-center space-x-2"
                                >
                                  <Upload className="h-4 w-4" />
                                  <span>Upload Document</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Error Message */}
                  {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
                      <AlertTriangle className="h-5 w-5 mr-2" />
                      <span>{error}</span>
                    </div>
                  )}
                  
                  {/* Submit Button */}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <Save className="h-5 w-5" />
                          <span>Submit Registration</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-8">
              {/* Instructions */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Registration Instructions</h2>
                <div className="space-y-4 text-gray-600">
                  <p>
                    This form is used by acquisition agents to register new suppliers for the Made in BD platform.
                  </p>
                  <ol className="space-y-3 list-decimal pl-5">
                    <li>Fill in all required business information accurately.</li>
                    <li>Take a photo of the business premises or representative.</li>
                    <li>Upload a business registration document or trade license.</li>
                    <li>Submit the form to generate a QR code for verification.</li>
                    <li>The compliance team will scan the QR code to verify the business.</li>
                  </ol>
                </div>
              </div>
              
              {/* Required Documents */}
              <div className="bg-orange-50 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Required Documents</h2>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="bg-orange-100 p-1 rounded-full mt-0.5">
                      <CheckCircle className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Trade License</p>
                      <p className="text-sm text-gray-600">Valid business registration document</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-orange-100 p-1 rounded-full mt-0.5">
                      <CheckCircle className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">TIN Certificate</p>
                      <p className="text-sm text-gray-600">Tax Identification Number document</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-orange-100 p-1 rounded-full mt-0.5">
                      <CheckCircle className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">VAT Registration</p>
                      <p className="text-sm text-gray-600">If applicable for the business</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-orange-100 p-1 rounded-full mt-0.5">
                      <CheckCircle className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Export License</p>
                      <p className="text-sm text-gray-600">For businesses engaged in export</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Contact Support */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Need Help?</h2>
                <p className="text-gray-600 mb-4">
                  If you encounter any issues during the registration process, please contact our support team.
                  {emailSent && ' A verification email has been sent to the representative.'}
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-blue-600" />
                    <span className="text-gray-700">+88 01770 156 736</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <span className="text-gray-700">support@madeinbd.com</span>
                  </div>
                </div>
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-500">
                    Have questions? Contact us at{' '}
                    <a href="mailto:suppliers@madeinbd.com" className="text-orange-600 hover:text-orange-700 font-medium">
                      suppliers@madeinbd.com
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantAcquisitionForm;